import axios from "axios";
import { CONFIG } from "../config";

// Create central axios instance
const api = axios.create({
    baseURL: CONFIG.API_URL,
    headers: {
        "Content-Type": "application/json",
        "x-csrf-protection": "antaryatra_v1" // Match backend requirement
    },
    withCredentials: true, // IMPORTANT: Sends HttpOnly cookies
});

const isDev = import.meta.env.DEV;
const apiBase = CONFIG.API_HOST;
const isStaging = typeof window !== "undefined" && (window.location.hostname.includes("stag") || window.location.hostname.includes("vercel.app"));
const isLeakingProd = isStaging && apiBase && apiBase.includes("api.antaryatra-app.com") && !apiBase.includes("stag");

if (isLeakingProd) {
    console.error("🚨 [ENVIRONMENT LEAK] Staging is talking to Production API! Update VITE_API_BASE in Vercel.");
}
console.log(`🔌 [ANTARYATRA] CONNECTING TO: ${apiBase} | SITE: ${CONFIG.SITE_URL} | MODE: ${isDev ? 'DEV' : (isStaging ? 'STAGING' : 'PRODUCTION')}`);

// Request Interceptor: Attach Token (Currently unused since we use Cookies)
// Keeping the interceptor shell in case we need it for CSRF tokens later
api.interceptors.request.use(
    (config) => {
        // Cookies are sent automatically due to withCredentials: true
        return config;
    },
    (error) => Promise.reject(error)
);

// Response Interceptor: Handle 401 (Expire) -> Refresh -> Retry
api.interceptors.response.use(
    (response) => response, // Success: Just valid response
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            // CRITICAL: Avoid infinite loop if refresh endpoint itself fails
            // ALSO avoid hijacking 401 errors from /login or /check-email
            if (
                originalRequest.url.includes("/auth/refresh") ||
                originalRequest.url.includes("/auth/login") ||
                originalRequest.url.includes("/auth/logout") ||
                originalRequest.url.includes("/auth/check-email")
            ) {
                return Promise.reject(error);
            }

            originalRequest._retry = true;

            try {
                // Call refresh endpoint
                await api.post("/auth/refresh");

                // The backend automatically set the new `accessToken` cookie.
                // We just retry the original request.
                return api(originalRequest);
            } catch (refreshError) {
                // Refresh failed (token invalid, expired, etc.)
                console.error("Session expired:", refreshError);
                return Promise.reject(refreshError);
            }
        }

        // Humanize Errors Globally
        let friendlyError = error;

        if (!error.response) {
            // Network / Offline
            friendlyError = new Error("We can't reach the server right now. Please check your connection.");
            friendlyError.original = error;
        } else if (error.response.status >= 500) {
            // Server Crash
            friendlyError = new Error("Something went wrong on our end. Please try again in a moment.");
            friendlyError.original = error;
            friendlyError.response = error.response; // Keep response for status checks
        }

        return Promise.reject(friendlyError);
    }
);

export default api;
